import { CALL_STATUS_NAMES } from '@/constants/call';
import { WIDGET_IDS } from '@/constants/widget';
import { useWidgetsData } from '@/hooks/api/useWidgetsData';
import { cn } from '@/lib/utils';
import { convertLocalTimezone } from '@/utils/dateUtils';
import { secondsToHms } from '@/utils/timeUtils';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDashboard } from '../../../contexts/DashboardContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Portal } from "@radix-ui/react-tooltip";
import styles from './Timeline.module.css';

export interface TimelineEvent {
  title: string;
  description?: string;
  time: string;
  highlight?: boolean;
  isConnected?: boolean;
}

export interface TimelineProps {
  waitingTime?: string;
  talkingTime?: string;
  events?: TimelineEvent[] | JourneyStreamDetail[];
  callId?: string
}

// Định nghĩa lại type dựa trên Angular
export interface JourneyStreamDetail {
  eventType: string;
  names?: string;
  startedAt: string;
  inPeriodId?: number;
  inPeriodType?: string;
  inPeriodDuration?: number;
}

export interface ResolveJourneyList {
  // backgroundColor: string;
  // color: string;
  duration: number;
  displayDurationSeconds: number;
  hasDuration: boolean;
  flexBoxNumber: number;
  textLabel: string;
  isWaitingTime?: boolean;
  isVoicemail?: boolean;
}

function getDurationFromStartedAt(startedAt?: string, endedAt?: string): number {
  if (!startedAt || !endedAt) return 0;
  const startMs = new Date(startedAt).getTime();
  const endMs = new Date(endedAt).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0;
  return Math.floor((endMs - startMs) / 1000);
}

// Hàm format thời gian (secondsToHms)
function secondsToHms(d: number = 0) {
  d = Number(d);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor(d % 60);
  return [
    h.toString().padStart(2, '0'),
    m.toString().padStart(2, '0'),
    s.toString().padStart(2, '0')
  ].join(':');
}

const Timeline: React.FC<TimelineProps> = ({ waitingTime, talkingTime, callId }) => {
  const [events, setEvents] = useState<JourneyStreamDetail[]>([]);

  // Memoize the params object to prevent unnecessary re-renders
  const widgetParams = useMemo(() => ({
    callId,
    widgetIds: [WIDGET_IDS.CALL_JOURNEY]
  }), [callId]);

  const { data, loading: widgetsLoading, error: widgetsError } = useWidgetsData(widgetParams);

  useEffect(() => {
    if (data?.callJourney) {
      setEvents(data.callJourney);
    }
  }, [data]);

  const { theme } = useDashboard();
  // Calculate width for each segment
  const nodeCount = events.length;
  const percentPerNode = 100 / nodeCount;

  // Find connected start and end indices
  // const connectedStartIndex = events.findIndex(event => event.isConnected);
  // const connectedEndIndex = events.length - 1 - [...events].reverse().findIndex(event => event.isConnected);

  // For each node, calculate the vertical distance to the box below
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const boxRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lineHeights, setLineHeights] = useState<number[]>([]);

  useEffect(() => {
    const heights = events.map((_, idx) => {
      const node = nodeRefs.current[idx];
      const box = boxRefs.current[idx];
      if (node && box) {
        const nodeRect = node.getBoundingClientRect();
        const boxRect = box.getBoundingClientRect();
        return boxRect.top - (nodeRect.top + nodeRect.height);
      }
      return 32;
    });
    setLineHeights(heights);
  }, [events.length]);

  // State cho box tổng hợp thời gian
  const [listResolveJourney, setListResolveJourney] = useState<ResolveJourneyList[]>([]);
  // State cho event chi tiết
  const [dataRealTime, setDataRealTime] = useState<JourneyStreamDetail[]>([]);
  const [previousJourneyData, setPreviousJourneyData] = useState<JourneyStreamDetail[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Gom nhóm event khi dataJourney thay đổi
  useEffect(() => {
    if (!events) return;
    setDataRealTime(events);
    setPreviousJourneyData(events);
    setListResolveJourney(handlerOnEventJourney(events));
    // Scroll về cuối
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
      }
    }, 100);
  }, [events]);

  // Gom nhóm event theo inPeriodId
  function handlerOnEventJourney(data: JourneyStreamDetail[]): ResolveJourneyList[] {
    const result: ResolveJourneyList[] = [];
    let currentGroup: JourneyStreamDetail[] = [];
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (currentGroup.length === 0) {
        currentGroup.push(item);
      } else {
        const lastItem = currentGroup[currentGroup.length - 1];
        if (item.inPeriodId === lastItem.inPeriodId) {
          currentGroup.push(item);
        } else {
          result.push(createGroupedItem(currentGroup, item));
          currentGroup = [item];
        }
      }
    }
    if (currentGroup.length > 0) {
      result.push(createGroupedItem(currentGroup));
    }
    return normalizeDisplayedDurations(result);
  }

  /**
   * Keep displayed segment durations consistent with the total floored duration.
   * API durations can be fractional seconds; flooring each segment independently
   * may cause a visible mismatch when users add them manually.
   */
  function normalizeDisplayedDurations(items: ResolveJourneyList[]): ResolveJourneyList[] {
    let cumulativeRawSeconds = 0;
    let cumulativeDisplayedSeconds = 0;

    return items.map((item) => {
      cumulativeRawSeconds += Math.max(0, Number(item.duration) || 0);
      const nextDisplayedTotal = Math.floor(cumulativeRawSeconds);
      const displayDurationSeconds = Math.max(0, nextDisplayedTotal - cumulativeDisplayedSeconds);
      cumulativeDisplayedSeconds = nextDisplayedTotal;

      return {
        ...item,
        displayDurationSeconds,
      };
    });
  }

  // Summarize timeline event
  function createGroupedItem(group: JourneyStreamDetail[], nextItem?: JourneyStreamDetail): ResolveJourneyList {
    const isWaitingTime = group[0].inPeriodType === 'Waiting time';
    const summedDuration = group.reduce((sum, item) => sum + (item.inPeriodDuration || 0), 0);
    const waitingDuration = getDurationFromStartedAt(group[0]?.startedAt, nextItem?.startedAt);
    const duration = isWaitingTime && waitingDuration >= 0 ? waitingDuration : summedDuration;
    const isVoicemail = group[0].inPeriodType === 'Voicemail';
    // Show segment even at 0s for Waiting and Voicemail so UI matches checkbox / events (same idea as waiting).
    const hasDuration = (duration && duration > 0) || isWaitingTime || isVoicemail;
    const flexBoxNumber = group.length;
    const textLabel = group[0].inPeriodType || '';
    return {
      duration,
      displayDurationSeconds: Math.max(0, Math.floor(duration)),
      hasDuration,
      flexBoxNumber,
      textLabel,
      isWaitingTime,
      isVoicemail
    };
  }

  // Helper function to check if event is active
  const isActiveEvent = (eventType: string) => {
    return eventType === 'Connected' || eventType === 'Transferred' || eventType === 'Resume';
  };

  return (
    <div className="container-journey bg-white dark:bg-[#3C5C73]" style={{ display: 'flex', alignItems: 'center', height: '130px', paddingLeft: '10px' }}>
      {widgetsLoading ? (
        <div className='w-full h-full flex items-center justify-center'>Loading...</div>
      ) : widgetsError ? (
        <div>Error loading timeline data</div>
      ) : (
        <div
          style={{
            height: '100%',
            scrollBehavior: 'smooth',
            overflow: 'auto',
            padding: '0 5px',
          }}
          ref={scrollContainerRef}
        >
          {/* Timeline summary */}
          <div className='flex ml-[10px]'>
            {listResolveJourney.map((item, idx) => (
              <div
                key={idx}
                className={cn(
                  item.hasDuration
                    ? item.isVoicemail ? 'bg-[#6D8596]'
                      : item.isWaitingTime ? (theme === 'dark' ? 'bg-[#1681F6]' : 'bg-[#0C3451]') : 'bg-[#00FEC1]'
                    : 'bg-transparent',
                  item.hasDuration
                    ? item.isVoicemail ? 'text-white'
                      : item.isWaitingTime ? 'text-white' : 'text-[#0c3451]'
                    : 'text-transparent'
                )}
                style={{
                  borderRadius: 8,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 8,
                }}
              >
                <div
                  style={{
                    paddingLeft: 10,
                    width:
                      item.flexBoxNumber > 1
                        ? item.flexBoxNumber > 3
                          ? item.flexBoxNumber * 198
                          : item.flexBoxNumber * 196
                        : 192,
                  }}
                >
                  <div className="text-left text-xs">{item.textLabel} <span className="font-bold">{secondsToHms(item.displayDurationSeconds)}</span></div>
                </div>
              </div>
            ))}
          </div>
          {/* Timeline event */}
          <div>
            <ul style={{ display: 'flex' }}>
              {dataRealTime.map((item, idx) => (
                <li
                  key={idx}
                  style={{
                    position: 'relative',
                    height: 3,
                    listStyleType: 'none',
                    width: 200,
                    backgroundColor: isActiveEvent(item.eventType)
                      ? '#00FEC1'
                      : theme === 'dark' ? 'white' : '#0C3451',
                    zIndex: 2,
                    marginTop: 8
                  }}
                  className={styles.timelineDot}
                >
                  <div className={styles.content}
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      top: 3,
                      ['--timeline-callout-line-color' as any]: theme === 'dark' ? 'white' : '#0C3451'
                    }}>
                    <div
                      style={{
                        backgroundColor: isActiveEvent(item.eventType)
                          ? 'rgb(231, 242, 254)'
                          : theme === 'dark' ? '#1681F6' : 'rgb(244, 244, 244)',
                        border: `${theme === 'dark' ? '4px solid #3C5C73' : '4px solid white'}`,
                        color: isActiveEvent(item.eventType) || theme !== 'dark' ? '#0c3451' : 'white',
                        padding: '4px 8px 6px',
                        borderRadius: 8,
                        borderTopLeftRadius: 0,
                        width: 200,
                        textAlign: 'left'
                      }}
                    >
                      <h3 className="m-0 text-xs font-bold">
                        <time style={{ textOverflow: 'ellipsis' }}>{item.eventType}</time>
                      </h3>
                      {item.eventType !== CALL_STATUS_NAMES.HANG_UP && (
                        <>
                          <TooltipProvider>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <p
                                  className={styles.textNameOverflow}
                                  style={{
                                    fontSize: 12,
                                    margin: 0,
                                    width: '100%',
                                    maxWidth: '180px',
                                    cursor: 'pointer',
                                    padding: 0
                                  }}
                                >
                                  {item.names}
                                </p>
                              </TooltipTrigger>
                              <Portal>
                                <TooltipContent
                                  side="top"
                                  sideOffset={8}
                                  collisionPadding={100}
                                >
                                  <p className="max-w-xs text-xs">
                                    {item.names}
                                  </p>
                                </TooltipContent>
                              </Portal>
                            </Tooltip>
                          </TooltipProvider>
                        </>
                      )}
                      <div style={{ fontWeight: 'bold', fontSize: 12 }}>{convertLocalTimezone(item.startedAt)}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )
      }
    </div >
  );
};

export default Timeline; 